import { fabric } from 'fabric';
import {
  hasFrameMemberBehaviors,
  hasLabelBehaviors,
  isComment,
  isFrameLike,
} from '../objects/guards';
import { type StyleOverride } from './ObjectBehaviors';
import SimpleGroup from './SimpleGroup';

interface SimpleSelection extends SimpleGroup {}

const getContainerForObjects = (
  objects: fabric.Object[]
): RecordId | undefined => {
  if (!objects.every(obj => hasFrameMemberBehaviors(obj))) return;

  const container = objects[0].container;
  if (!objects.every(obj => obj.container === container)) return;
  return container;
};

// Replacement for the Fabric selection class.
class SimpleSelection extends SimpleGroup {
  type = 'activeSelection';

  constructor(
    objects: fabric.Object[],
    options: { locked?: true } & Record<string, unknown>
  ) {
    super(objects, options);

    this.controller = objects[0]?.controller;

    this.setReadOnly(this.canvas && this.canvas.readOnly);
  }

  toGroup(): SimpleGroup | undefined {
    const objects = this._objects.slice().filter(object => !isComment(object));

    // If only comments are selected, don't do anything. This is an extra layer
    // of protection if the selection is not properly filtered.
    if (objects.length === 0) return;

    this._objects = [];
    const options = fabric.Object.prototype.toObject.call(this);
    const newGroup = new SimpleGroup([], { controller: this.controller });
    delete options.type;
    newGroup.set(options);
    let targetZ: number | null = null;
    objects.forEach(object => {
      // Record the highest z-index and insert the new group there
      const objectZ = object.z;
      if (!targetZ || objectZ > targetZ) targetZ = objectZ;
      object.canvas.remove(object);
      // This doesn't affect the double click functionality, but prevents the
      // element from being selected through a selection box.
      object.selectable = false;
      object.simpleGroup = newGroup;
    });
    newGroup._objects = objects;
    newGroup.setRotationControlVisibility();
    if (!this.canvas) {
      return newGroup;
    }
    const canvas = this.canvas;
    canvas.addObjects(
      [newGroup],
      targetZ
        ? {
            where: { where: 'after', targetZ },
          }
        : undefined
    );
    const container = getContainerForObjects(objects);
    if (container) {
      newGroup.set({ container });
    }
    canvas._activeObject = newGroup;
    newGroup.prepareForTransform();
    newGroup.setCoords();
    newGroup.fire('created');
    return newGroup;
  }

  onDeselect() {
    this.destroy();
    return false;
  }

  _renderControls(
    ctx: CanvasRenderingContext2D,
    styleOverride: StyleOverride,
    childrenOverride?: StyleOverride
  ) {
    ctx.save();
    ctx.globalAlpha = this.isMoving ? this.borderOpacityWhenMoving : 1;
    super._renderControls(ctx, styleOverride);
    childrenOverride = childrenOverride || {};
    if (typeof childrenOverride.hasControls === 'undefined') {
      childrenOverride.hasControls = false;
    }
    childrenOverride.forActiveSelection = true;
    if (!this.isMoving) {
      for (let i = 0, len = this._objects.length; i < len; i++) {
        this._objects[i]._renderControls(ctx, childrenOverride);
      }
    }
    ctx.restore();
  }

  // If we selected locked objects, set the border color to inidicate this
  handleLockedObjectSelection() {
    const hasLockedObjects = this._objects.some(object => object.locked);
    this.borderColor = hasLockedObjects
      ? this.lockedBorderColor
      : this.unlockedBorderColor;
  }

  removeWithUpdate(object: fabric.Object) {
    super.removeWithUpdate(object);
    object.fire?.('deselected');

    // If the group is empty, remove it. This can only happen with an active
    // selection.
    if (this._objects.length === 0) {
      this.canvas.discardActiveObject();
      this.canvas.fire('selection:cleared', { target: this });
    } else if (this._objects.length === 1) {
      this._selectRemainingObject();
    }

    return this;
  }

  // If there is only one object left, get rid of this selection and select
  // the single object instead. This removes group controls and the group
  // selection box
  private _selectRemainingObject() {
    if (this._objects.length === 1) {
      this.canvas.discardActiveObject();
      this.canvas.fire('selection:cleared', { target: this });
      this.canvas.setActiveObject(this._objects[0]);
    }
  }

  objectInSelection(object?: fabric.Object): boolean {
    if (!object) {
      return false;
    }
    return this._objects.includes(object);
  }

  // Provides consistent logic for selecting objects across different actions (
  // e.g. ToolSelectAll, groupSelector, etc.). Some objects require special handling
  // in selections
  static getFilteredObjectsForSelection(objects: fabric.Object[]) {
    // exclude connector labels from selections to avoid double delete
    if (objects.length > 1) {
      objects = objects.filter(
        obj => !(hasLabelBehaviors(obj) && obj.connector)
      );
    }

    // Prioritize selecting unlocked objects if there are a mix of locked and
    // unlocked objects in a selection
    const hasLockedObjects = objects.some(object => object.locked);
    const hasUnlockedObjects = objects.some(object => !object.locked);

    if (hasLockedObjects && hasUnlockedObjects) {
      objects = objects.filter(obj => !obj.locked);
    }

    // filter comments
    objects = objects.filter(obj => !isComment(obj));

    // Only select visible objects - e.g., resolved comments
    objects = objects.filter(obj => obj.visible);

    // Do not allow children and their parent container in the same selection
    // find all the frames in objects and filter out their children
    const frames = objects.filter(isFrameLike);
    const frameChildren = frames.flatMap(frame => frame.children);
    objects = objects.filter(obj => !frameChildren.includes(obj));

    return objects;
  }
}

export default SimpleSelection;
